import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, Observable, of, switchMap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { SynchronizedTokenService } from '../services/auth/synchronized-token.service';
import { AuthService } from '../services/auth/auth.service';
import { HttpStatusCodes } from '../constants/http-status-codes';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  //we need to avoid refreshing token for these urls
  //as they should be accessible without token
  private readonly urlsToNotRefresh: string[] = [
    'auth/token',
    'auth/forgot-password',
    'auth/reset-password',
    'auth/refresh'
  ];

  constructor(
    private authService: AuthService,
    private _tokenService: SynchronizedTokenService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //skip svg icons as mat icon request them via xhr
    if (!request.url.includes('api/')) {
      return next.handle(request);
    }

    if (this.urlsToNotRefresh.find(url => request.url.includes(url))) {
      //avoid infinite loop
      return next.handle(this.getNewRequest(request, this.authService.getAccessToken()));
    }

    const token$ = this.authService.isAccessTokenRequiresRefresh()
      ? this._tokenService.refreshToken()
      : of(this.authService.getAccessToken());

    return token$.pipe(
      catchError((err) => this.onRefreshFailed(err)),
      switchMap((token) => next.handle(this.getNewRequest(request, token)).pipe(
        catchError((err) => this.isUnauthorized(err)
          ? this.retryWithNewToken(request, next, token, err)
          : throwError(() => err))
      ))
    );
  }

  /**
   * LBRD-6384: the API rejected the token (revoked, malformed, expired early). Refresh once --
   * shared with any other request that hit the same 401 -- and replay the request. A 401 on the
   * replay is not retried again; it propagates as is.
   */
  private retryWithNewToken(
    request: HttpRequest<any>,
    next: HttpHandler,
    rejectedToken: string | null,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (!this.authService.getRefreshToken()) {
      // No session to renew (logged out meanwhile, or never had one).
      return this.authService.isLoggedIn
        ? this.endSession()
        : throwError(() => originalError);
    }

    return this._tokenService.refreshAfterUnauthorized(rejectedToken).pipe(
      catchError((err) => this.onRefreshFailed(err)),
      switchMap((token) => next.handle(this.getNewRequest(request, token)))
    );
  }

  /**
   * A failed refresh that ended the session (AuthService shows "Session expired" and redirects to
   * Login) completes the request silently, so components don't stack generic error toasts on top
   * of the redirect. A refresh that failed for any other reason (offline, 5xx) left the session in
   * place -- that error propagates to the caller.
   */
  private onRefreshFailed(err: unknown): Observable<never> {
    return this.authService.isLoggedIn ? throwError(() => err) : EMPTY;
  }

  private endSession(): Observable<never> {
    this.authService.handleSessionExpired();
    return EMPTY;
  }

  private isUnauthorized(err: unknown): err is HttpErrorResponse {
    return err instanceof HttpErrorResponse && err.status === HttpStatusCodes.UNAUTHORIZED;
  }

  getNewRequest(
    request: HttpRequest<any>,
    authToken: string | null = null
  ): HttpRequest<any> {
    const correlationId = uuidv4();

    const headers = {
      'Authorization': '',
      'x-correlation-id': correlationId,
      'ngsw-bypass': ''
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return request.clone({
      setHeaders: headers
    });
  }
}
